/**
 * Icon Mapping Utility
 *
 * Maps string icon names (stored in Firestore) to MUI icon components.
 * This allows quiz icons to be configured remotely without code changes.
 *
 * Usage:
 * import { getIcon } from './iconMap';
 * const IconComponent = getIcon('PsychologyAlt');
 * <IconComponent />
 */

import React from "react";

// Import MUI icons used in quizzes
import PsychologyAltIcon from "@mui/icons-material/PsychologyAlt";
import BedIcon from "@mui/icons-material/Bed";
import WineBarIcon from "@mui/icons-material/WineBar";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import FavoriteIcon from "@mui/icons-material/Favorite";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import QuizIcon from "@mui/icons-material/Quiz";
import EmojiPeopleIcon from "@mui/icons-material/EmojiPeople";
import GroupsIcon from "@mui/icons-material/Groups";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import StarIcon from "@mui/icons-material/Star";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import SelfImprovementIcon from "@mui/icons-material/SelfImprovement";
import CelebrationIcon from "@mui/icons-material/Celebration";
import MoodIcon from "@mui/icons-material/Mood";
import InsightsIcon from "@mui/icons-material/Insights";
import ExploreIcon from "@mui/icons-material/Explore";
import PeopleIcon from "@mui/icons-material/People";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import NightsStayIcon from "@mui/icons-material/NightsStay";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import DirectionsRunIcon from "@mui/icons-material/DirectionsRun";
import SpaIcon from "@mui/icons-material/Spa";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import BalanceIcon from "@mui/icons-material/Balance";
import HandshakeIcon from "@mui/icons-material/Handshake";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAlt";

/**
 * Map of icon names to their MUI components
 * Add new icons here as needed for new quizzes
 */
const iconMap = {
  // Quiz-specific icons (currently used)
  PsychologyAlt: PsychologyAltIcon,
  Bed: BedIcon,
  WineBar: WineBarIcon,
  AccountBalance: AccountBalanceIcon,
  Favorite: FavoriteIcon,
  VolunteerActivism: VolunteerActivismIcon,

  // Additional icons for future quizzes
  Quiz: QuizIcon,
  EmojiPeople: EmojiPeopleIcon,
  Groups: GroupsIcon,
  LocalFireDepartment: LocalFireDepartmentIcon,
  Star: StarIcon,
  Lightbulb: LightbulbIcon,
  SelfImprovement: SelfImprovementIcon,
  Celebration: CelebrationIcon,
  Mood: MoodIcon,
  Insights: InsightsIcon,
  Explore: ExploreIcon,
  People: PeopleIcon,
  FavoriteBorder: FavoriteBorderIcon,
  NightsStay: NightsStayIcon,
  WbSunny: WbSunnyIcon,
  DirectionsRun: DirectionsRunIcon,
  Spa: SpaIcon,
  EmojiEvents: EmojiEventsIcon,
  AutoAwesome: AutoAwesomeIcon,
  Balance: BalanceIcon,
  Handshake: HandshakeIcon,
  ChatBubble: ChatBubbleIcon,
  SentimentSatisfiedAlt: SentimentSatisfiedAltIcon,
};

/**
 * Get an icon component by name
 * @param {string} iconName - The name of the icon (e.g., "PsychologyAlt")
 * @returns {React.Component} - The MUI icon component, or QuizIcon as default
 */
export const getIcon = (iconName) => {
  return iconMap[iconName] || QuizIcon;
};

/**
 * Get a rendered icon element by name
 * @param {string} iconName - The name of the icon
 * @param {object} props - Props to pass to the icon component
 * @returns {React.Element} - The rendered icon element
 */
export const renderIcon = (iconName, props = {}) => {
  const IconComponent = getIcon(iconName);
  return React.createElement(IconComponent, props);
};

/**
 * Get list of all available icon names
 * Useful for admin interfaces or documentation
 * @returns {string[]} - Array of available icon names
 */
export const getAvailableIcons = () => {
  return Object.keys(iconMap);
};

/**
 * Check if an icon name is valid/available
 * @param {string} iconName - The icon name to check
 * @returns {boolean}
 */
export const isValidIcon = (iconName) => {
  return iconName in iconMap;
};

export default iconMap;
